/**
 * Pricing Rules types for Partner Dashboard
 *
 * Core types are re-exported from generated admin types.
 * Execution types define GraphQL request/response shapes.
 */

import type { RewareAdminTypes } from '@troverecommerce/shared-reware-api';

// Re-export core types from generated admin types
export type PricingRule = RewareAdminTypes.PricingDiscountCriteriaAdmin;
export type PricingRuleInput = RewareAdminTypes.PricingRuleInput;
export type PricingRuleUpdateInput = RewareAdminTypes.PricingRuleUpdateInput;
export type PricingRuleEdge = RewareAdminTypes.PricingRuleEdge;
export type PricingRulesConnection = RewareAdminTypes.PricingRuleConnection;
export type PageInfo = RewareAdminTypes.PricingRulePageInfo;
export type BatchCreateResult = RewareAdminTypes.BatchCreateResult;
export type BatchCreateError = RewareAdminTypes.BatchCreateError;
export type DeletePricingRuleResult = RewareAdminTypes.DeletePricingRuleResult;
export type PricingRulePositionResult = RewareAdminTypes.PricingRulePositionResult;

// Dashboard-specific types

export const PRICING_TYPES = ['yerdle_price', 'condition'] as const;
export type PricingType = (typeof PRICING_TYPES)[number];

export const PRICING_TYPE_LABELS: Record<PricingType, string> = {
  yerdle_price: 'Yerdle Price',
  condition: 'Condition Discount',
};

export const SOURCE_COLUMNS = [
  'price',
  'msrp_price',
  'last_known_retail_price',
  'last_known_outlet_price',
  'absolute_price',
] as const;

export interface PricingFilters {
  department?: string;
  category?: string;
  tag?: string;
  name?: string;
  merchantCategory?: string;
  upc?: string;
  condition?: string;
}

export interface StartPricingImportResult {
  pricingDiscountId: number;
}

// GraphQL execution types

export interface FetchPricingRulesExecVariables {
  partnerId: number;
  pricingType: string;
  first?: number;
  after?: string | null;
  filters?: PricingFilters;
}

export interface FetchPricingRulesExecResponse {
  partner: {
    pricingRules: PricingRulesConnection;
  };
}

export interface CreatePricingRuleExecVariables {
  partnerId: number;
  pricingType: string;
  rule: PricingRuleInput;
}

export interface CreatePricingRuleExecResponse {
  partner: {
    createPricingRule: PricingRule;
  };
}

export interface UpdatePricingRuleExecVariables {
  partnerId: number;
  ruleId: number;
  updates: PricingRuleUpdateInput;
}

export interface UpdatePricingRuleExecResponse {
  partner: {
    updatePricingRule: PricingRule;
  };
}

export interface DeletePricingRuleExecVariables {
  partnerId: number;
  ruleId: number;
}

export interface DeletePricingRuleExecResponse {
  partner: {
    deletePricingRule: DeletePricingRuleResult;
  };
}

export interface UpdatePricingRulePositionExecVariables {
  partnerId: number;
  ruleId: number;
  newPosition: number;
}

export interface UpdatePricingRulePositionExecResponse {
  partner: {
    updatePricingRulePosition: PricingRulePositionResult;
  };
}

export interface StartPricingImportExecVariables {
  partnerId: number;
  pricingType: string;
}

export interface StartPricingImportExecResponse {
  partner: {
    startPricingImport: StartPricingImportResult;
  };
}

export interface AddToPricingImportExecVariables {
  partnerId: number;
  pricingDiscountId: number;
  rules: PricingRuleInput[];
}

export interface AddToPricingImportExecResponse {
  partner: {
    addToPricingImport: BatchCreateResult;
  };
}

// Pricing history (existing reware-api query — type already in generated admin types)
// Actual generated shape: `createdAt` is non-nullable (typed as `any` via Date scalar);
// `partnerId`, `userId`, `initiatedAt`, `processedAt`, `user` are all nullable.
export type PricingHistoryEntry = RewareAdminTypes.PricingDiscountHistoryAdmin;

export interface FetchPricingHistoryExecVariables {
  partnerId: number;
  pricingType: PricingType;
}

// Honor the generated schema's nullability — both the array and its elements are
// `Maybe<…>`. fetchPricingHistory rejects the outer-null case (diagnostic Error)
// and filters out any null elements before returning.
export interface FetchPricingHistoryExecResponse {
  partner: {
    pricingHistory: (PricingHistoryEntry | null)[] | null;
  };
}

// Resale pricing export (new reware-api mutation — type NOT yet in generated admin
// types; defined inline here until shared-reware-api regenerates after the reware-api
// PR ships). Two-field record, drift risk is near-zero.
//
// FIXME(ENG-29766): Replace with the generated
// `RewareAdminTypes.GenerateResalePricingExportResult` (or its real name) once
// shared-reware-api regenerates. Expect the generated type to model `expiresAt`
// as `Maybe<…>`; widen the runtime guard at the call site accordingly.
export interface PricingExportResult {
  url: string;
  expiresAt: string;
}

export interface GenerateResalePricingExportExecVariables {
  partnerId: number;
  id: number;
}

export interface GenerateResalePricingExportExecResponse {
  partner: {
    generateResalePricingExport: PricingExportResult;
  };
}
